import { Button, Card, ConfirmModal, Modal, Panel } from '@/components/Bicester';
import { Form, FormControl } from '@/components/formik-rsuite';
import { useRequest } from 'ahooks';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import Toolbar from '../Toolbar';
import { Alert } from 'rsuite';
import {
  fetchConfirmReceipt,
  fetchReceiptMemberInfo,
  fetchScanReceipt,
  fetchUploadReceipt,
} from '@/services/receipt/upload';
import PageLoader from '@/components/shared/PageLoader';
import useLoading from '@/hooks/useLoading';
import useSiteBrandList from '@/hooks/preload/useSiteBrandList';
import { IconButton } from 'rsuite5';
import Image from '@/components/Bicester/Image';
import UploadBasicInfo from './UploadBasicInfo';
import { defaultUploadInfo, uploadReceiptDRTSchema, uploadReceiptSchema } from './constants';
import useGePcsList from '@/hooks/preload/useGePcsList';
import { useReadyInfo } from '@/providers/ReadyProvider';
import { LEVEL_TYPE, ROLE_TYPE } from '@/constants';
import _ from 'lodash';
import { SERVICE_STATUS } from '@/services/constants';
import useSalesSource from '@/hooks/preload/useSalesSource';
import useRouter from '@/hooks/useRouter';
import { useModal } from '@ebay/nice-modal-react';
import { SearchParams } from '../../Upload';
import { BICESTER_FULL_DATE } from '@/constants/dateFormat';
import dayjs from 'dayjs';
import * as Yup from 'yup';

const UploadReceipt = ({
  setSearchParams,
}: {
  setSearchParams: (searchParams: SearchParams) => void;
}) => {
  const modal = useModal(ConfirmModal);
  const { query } = useRouter();
  const { profile } = useReadyInfo();
  const { data: brandList, loading: brandListLoading } = useSiteBrandList();
  const { data: gePcsList, loading: gePcsListLoading } = useGePcsList();
  const { data: salesSource, loading: salesSourceLoading } = useSalesSource();

  const [defaultInfo, setDefaultInfo] = useState(defaultUploadInfo);
  const [currentMember, setCurrentMember] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { run: getReceiptMemberInfo, loading: receiptMemberInfoLoading } = useRequest(
    fetchReceiptMemberInfo,
    { manual: true }
  );

  const { run: uploadReceipt, loading: uploadReceiptLoading } = useRequest(fetchUploadReceipt, {
    manual: true,
  });

  const { run: confirmReceipt, loading: confirmReceiptLoading } = useRequest(fetchConfirmReceipt, {
    manual: true,
  });

  const { run: scanReceipt, loading: scanReceiptLoading } = useRequest(fetchScanReceipt, {
    manual: true,
  });

  useEffect(() => {
    if (query?.memberid) {
      const { memberid } = query;
      getReceiptMemberInfo({ member_id: memberid }).then((res) => {
        if (res?.data?.id) {
          if (res?.data?.level !== LEVEL_TYPE.NOT) {
            Alert.success('匹配成功');
            formik.setFieldValue('member_id', res.data.id);
            setDefaultInfo({ ...defaultInfo, member_id: res.data.id });
            setCurrentMember(res.data);
          } else {
            Alert.error('会员不存在');
          }
        } else {
          Alert.error('会员不存在');
        }
      });
    }
  }, []);

  const formik = useFormik({
    initialValues: defaultInfo,
    validationSchema:
      profile?.role === ROLE_TYPE.DRT ? uploadReceiptDRTSchema : uploadReceiptSchema,
    enableReinitialize: true,
    onSubmit(values, actions) {
      const data = Object.assign({}, values, {
        receipts: JSON.stringify(values.receipts),
      });
      if (values.receipts.filter((receipt) => Number(receipt.price) >= 30000).length) {
        modal.show({
          contentText: '小票金额超过30000，请确认上传小票金额？',
          submitText: '确认',
          cancelText: '修改',
          onSubmit: () => {
            handleUploadReceipt(data, values, actions);
          },
        });
      } else {
        handleUploadReceipt(data, values, actions);
      }
    },
  });
  const qrFormik = useFormik({
    initialValues: { code: '' },
    validationSchema: Yup.object({
      code: Yup.string().ensure().required('请输入订单号'),
    }),
    onSubmit: (values, actions) => {
      scanReceipt({ member_id: formik.values?.member_id, code: values.code })
        .then((res) => {
          if (res?.status === SERVICE_STATUS.SUCCESS) {
            Alert.success('小票上传成功');
            setShowModal(false);
            qrFormik.resetForm();
          }
        })
        .catch((e) => {
          actions.setErrors(e.formError);
        });
    },
  });

  const matchMember = () => {
    fetchReceiptMemberInfo({
      phone: formik.values?.phone,
      user_code: formik.values?.user_code,
    }).then((res) => {
      if (res?.status === SERVICE_STATUS.SUCCESS) {
        if (res?.data?.id) {
          if (res?.data?.level !== LEVEL_TYPE.NOT) {
            Alert.success('匹配成功');
            formik.setFieldValue('member_id', res.data.id);
            setCurrentMember(res.data);
            const today = dayjs(new Date()).format(BICESTER_FULL_DATE);
            setSearchParams({
              phone: formik.values?.phone,
              user_code: formik.values?.user_code,
              time_range: [today, today],
            });
          } else {
            Alert.error('会员不存在');
          }
        } else {
          Alert.error('会员不存在');
        }
      }
    });
  };

  /**
   * 上传小票操作
   * @param uploadData fetchUploadReceipt上传接口的参数
   * @param values formik的onSubmit的values参数
   * @param actions formik的onSubmit的actions参数
   * - 小票金额存在超过30000的弹窗提示
   * - 确认后调用fetchUploadReceipt上传接口
   * - 返回的is_confirm=1则弹窗提示该客人存在相同小票
   * - 确认后嗲用fetchConfirmReceipt确认接口
   */
  const handleUploadReceipt = (uploadData, values, actions) => {
    uploadReceipt(uploadData)
      .then((res) => {
        const { member_id = null, phone = '', user_code = '' } = formik.values;
        if (res?.data?.is_confirm === 1) {
          modal.show({
            contentText: '客人存在相同小票，请确认相同金额小票？',
            submitText: '确认',
            cancelText: '取消',
            onSubmit: () => {
              confirmReceipt({
                member_id: values.member_id,
                consume_time: values.consume_time,
              })
                .then((result) => {
                  if (result?.status === SERVICE_STATUS.SUCCESS) {
                    Alert.success('小票上传成功');
                    setSearchParams({
                      phone: res?.data?.phone || '',
                      user_code: res?.data?.user_code || '',
                    });
                    formik.resetForm();
                    formik.setValues({ ...defaultInfo, member_id, phone, user_code });
                  }
                })
                .catch((e) => {
                  actions.setErrors(e.formError);
                });
            },
          });
        } else {
          if (res?.status === SERVICE_STATUS.SUCCESS) {
            Alert.success('小票上传成功');
            setSearchParams({
              phone: res?.data?.phone || '',
              user_code: res?.data?.user_code || '',
            });
            formik.resetForm();
            formik.setValues({ ...defaultInfo, member_id, phone, user_code });
          }
        }
      })
      .catch((e) => {
        actions.setErrors(e.formError);
      });
  };

  const loading = useLoading(
    brandListLoading || gePcsListLoading || salesSourceLoading || receiptMemberInfoLoading
  );

  return (
    <>
      <FormikProvider value={formik}>
        {loading && <PageLoader />}
        <Form guard>
          <Panel
            leftShadow
            className="py-[25px]"
            loading={uploadReceiptLoading || confirmReceiptLoading}
          >
            <FieldArray name="receipts">
              {({ insert, remove, push }) => (
                <>
                  <Toolbar
                    title="上传小票"
                    handleReset={() => {
                      setDefaultInfo(defaultUploadInfo);
                      formik.resetForm();
                      setCurrentMember(null);
                    }}
                  />
                  <div className="flex">
                    <UploadBasicInfo currentMember={currentMember} />
                    <Card smDivider className="flex-1 flex flex-col">
                      <Card.Input label="手机号" formKey="phone" onPressEnter={matchMember} />
                      <Card.Input label="会员号" formKey="user_code" onPressEnter={matchMember} />
                      <Card.DatePicker required label="消费日期" formKey="consume_time" />
                      {profile?.role === ROLE_TYPE.DRT && (
                        <>
                          <Card.Picker
                            required
                            label="DRT销售"
                            formKey="pcs_id"
                            pickerData={gePcsList.pcs_list}
                            searchable
                          />
                          <Card.Picker
                            required
                            label="销售渠道"
                            formKey="sales_source"
                            pickerData={salesSource}
                            searchable
                          />
                        </>
                      )}
                    </Card>
                    <Card smDivider className="flex-[2] h-auto">
                      <div className="flex h-full">
                        <div className="w-[30px] h-[47px] ml-[10px] flex items-center">
                          <IconButton
                            style={{ width: 30, backgroundColor: 'transparent' }}
                            onClick={matchMember}
                          >
                            <Image src="/resources/images/match.svg" />
                          </IconButton>
                        </div>
                        <div className="flex-1 flex flex-col justify-between">
                          <div>
                            {formik.values.receipts.length > 0 &&
                              formik.values.receipts.map((item, index) => (
                                <div className="flex items-center" key={index}>
                                  <Card.Picker
                                    className="flex-[3]"
                                    required
                                    label="上传品牌"
                                    formKey={`receipts.${index}.brand`}
                                    pickerData={brandList}
                                    searchable
                                  />
                                  <Card.Input
                                    className="ml-[9px]"
                                    formKey={`receipts.${index}.price`}
                                    inputType="price"
                                    inputPrefix="￥"
                                  />
                                  {formik.values.receipts.length > 1 && (
                                    <IconButton className="ml-[5px]" onClick={() => remove(index)}>
                                      <Image src="/resources/images/delete.svg" />
                                    </IconButton>
                                  )}
                                </div>
                              ))}
                            <div className="flex justify-end mr-[22px]">
                              <IconButton
                                className="flex items-center"
                                onClick={() => push({ brand: '', price: '' })}
                              >
                                <Image src="/resources/images/add.svg" />
                                <div className="text-[12px] text-[#999999] ml-[5px]">新增小票</div>
                              </IconButton>
                            </div>
                          </div>
                          <div className="flex justify-end mt-[10px]">
                            {formik.values?.member_id && (
                              <Button
                                className="px-[70px] mr-[30px]"
                                onClick={() => setShowModal(true)}
                              >
                                扫码上传
                              </Button>
                            )}
                            <Button className="px-[70px]" onClick={() => formik.submitForm()}>
                              确认上传
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>
                </>
              )}
            </FieldArray>
          </Panel>
        </Form>
      </FormikProvider>
      <Modal
        show={showModal}
        isInput
        content={
          <>
            {scanReceiptLoading && <PageLoader />}
            <div className="flex justify-center h-[30px] leading-[30px] mb-[20px]">
              <span>小票二维码：</span>
              <FormikProvider value={qrFormik}>
                <Form className="flex-1">
                  <FormControl name="code" className="h-[30px]" />
                </Form>
              </FormikProvider>
            </div>
          </>
        }
        confirm="确认上传"
        cancel="取消"
        onConfirm={qrFormik.submitForm}
        onCancel={() => {
          setShowModal(false);
          qrFormik.resetForm();
        }}
      />
    </>
  );
};

export default UploadReceipt;
